/**
 * i18n barrel export — single import point for all pages.
 * Usage in any component:
 *   import { useT } from './i18n';
 *   function MyPage() {
 *     const t = useT();
 *     return <h1>{t('nav.dashboard')}</h1>;
 *   }
 */
import { useI18n } from './context';
import en from './en';
import zh from './zh';
import { createT } from './context';

const DICTIONARIES = { en, zh };

export function useT() {
  const { lang } = useI18n();
  const dict = DICTIONARIES[lang] || en;
  return createT(lang, dict);
}

// Returns the BCP-47 locale tag matching the current UI language,
// for use with Date.toLocaleString / toLocaleDateString.
export function useLocale() {
  const { lang } = useI18n();
  return lang === 'zh' ? 'zh-CN' : 'en-US';
}

// Non-hook version (for use outside components, reads localStorage directly).
export function appLocale() {
  try {
    const stored = localStorage.getItem('socioniq_lang');
    if (stored === 'zh') return 'zh-CN';
    if (stored === 'en') return 'en-US';
    return (navigator.language || '').startsWith('zh') ? 'zh-CN' : 'en-US';
  }
  catch { return 'en-US'; }
}

// Translate an axios API error to a localized user-facing message.
// Server sends { code, params } on coded errors; raw error text is the
// fallback for legacy endpoints not yet migrated.
export function translateApiError(err, t) {
  const data = err?.response?.data;
  if (data?.code) {
    const key = 'errors.' + data.code;
    const val = t(key, data.params || {});
    if (val !== key) return val;
  }
  return data?.error || err?.message || t('errors.INTERNAL_ERROR');
}


// Resolve a direction key (e.g. "sales", "ndis_care") to a localized name.
// Falls back to the provided fallback name (usually the DB direction_name)
// or the raw key when no translation exists. Reacts to language switches
// automatically because it depends on useT().
export function useDirectionName() {
  const t = useT();
  return (directionKey, fallback) => {
    if (!directionKey) return fallback || '';
    const localized = t('directions.' + directionKey);
    return localized !== 'directions.' + directionKey ? localized : (fallback || directionKey);
  };
}

// Re-export context for providers
export { LanguageProvider, useI18n } from './context';
